// src/pages/metrics/CRMMetrics.jsx
import React, { useEffect, useState, lazy, Suspense, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import BrandSpinner from "../../shared/components/BrandSpinner";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { useAuth } from "../../providers/AuthProvider";
import { useLocation } from "react-router-dom";
import api, { isCancel } from "../../services/api/axios";
import RouteLoader from "../../shared/components/RouteLoader";
import BackButton from "../../shared/components/BackButton";
import { HEADER_TITLE_SX } from "../../theme/header";

const CONTAINER_W = 1900;
const BRAND_BLUE = "#00336C";

// Lazy load charts
const CRMMetricsCharts = lazy(() => import("./CRMMetricsCharts"));

export default function CRMMetrics() {
  const { token } = useAuth();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const startParam = params.get("start");
  const endParam = params.get("end");

  const [startDate] = useState(
    startParam
      ? new Date(startParam)
      : new Date(new Date().getFullYear(), 0, 1),
  );

  const [endDate] = useState(
    endParam ? new Date(endParam) : new Date(new Date().getFullYear(), 11, 31),
  );

  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);

  // ================================
  // FETCH CRM METRICS
  // ================================
  useEffect(() => {
    if (!token) {
      setMetrics([]);
      setLoading(false);
      return;
    }

    const ac = new AbortController();

    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/metrics/crm", {
          params: {
            start: startDate.toISOString().split("T")[0],
            end: endDate.toISOString().split("T")[0],
          },
          signal: ac.signal,
        });

        setMetrics(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!isCancel(error)) {
          setMetrics([]);
        }
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [token, startDate, endDate]);

  // ================================
  // CUMULATIVE SERIES (COMPANIES + CONTACTS ONLY)
  // ================================
  const cumulativeSeries = useMemo(() => {
    const sorted = [...(metrics || [])].sort((a, b) => {
      const da = a?.month_start ? new Date(a.month_start) : new Date(0);
      const db = b?.month_start ? new Date(b.month_start) : new Date(0);
      return da - db;
    });

    let runCompanies = 0;
    let runContacts = 0;

    return sorted.map((m) => {
      runCompanies += m?.companies || 0;
      runContacts += m?.contacts || 0;

      return {
        month: m.month,
        month_start: m.month_start,
        total_companies: runCompanies,
        total_contacts: runContacts,
      };
    });
  }, [metrics]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 0, width: "100%" }}>
        <Box sx={{ px: 2 }}>
          <Box
            sx={{
              p: 3,
              width: "100%",
              borderRadius: 2,
              minHeight: "calc(100vh - 100px)",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* HEADER */}
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto" },
                alignItems: "center",
                mb: 2,
                px: 2,
                py: 1.5,
                border: `1px solid ${BRAND_BLUE}`,
                borderRadius: 2,
                background:
                  "linear-gradient(180deg, rgba(231,240,218,0.7) 0%, rgba(255,255,255,0.9) 100%)",
                position: "sticky",
                top: 0,
                zIndex: 2,
                backdropFilter: "blur(4px)",
              }}
            >
              {/* Back Button (Identical to ContactDetails style) */}
              <BackButton
                to={`/metrics?start=${startDate
                  .toISOString()
                  .slice(0, 10)}&end=${endDate.toISOString().slice(0, 10)}`}
              />

              <Typography
                sx={{
                  ...HEADER_TITLE_SX,
                  minWidth: { xs: "auto", md: 180 },
                  textAlign: "center",
                  color: BRAND_BLUE,
                }}
              >
                CRM Metrics
              </Typography>

              <Box sx={{ display: { xs: "none", md: "block" }, width: 96 }} />
            </Box>

            {/* CHARTS */}
            <Box
              sx={{
                width: "100%",
                maxWidth: CONTAINER_W,
                mx: "auto",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {loading ? (
                <BrandSpinner compact py={2} />
              ) : (
                <Suspense fallback={<RouteLoader label="Loading charts..." />}>
                  <CRMMetricsCharts
                    metrics={metrics}
                    cumulativeSeries={cumulativeSeries}
                  />
                </Suspense>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
